import React, { useEffect, useState } from "react";
import { CheckSquare, Square, Calendar, Plus, X, Save, Trash2, ClipboardList, Moon } from "lucide-react";
import http from "@/lib/http";
import { parseDate } from "@/lib/format";

/**
 * Card with all tasks linked to a single lead. Allows ticking off, deleting
 * and adding new tasks inline. Used inside LeadDetail page.
 */
export default function LeadTasks({ leadId }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDue, setDraftDue] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await http.get("/api/tasks", { params: { lead_id: leadId } });
      setTasks(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kon taken niet laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (leadId) load(); /* eslint-disable-next-line */ }, [leadId]);

  const toggle = async (task) => {
    await http.patch(`/api/tasks/${task.id}`, { completed: !task.completed });
    await load();
  };
  const remove = async (task) => {
    if (!window.confirm(`Taak "${task.title}" verwijderen?`)) return;
    await http.delete(`/api/tasks/${task.id}`);
    await load();
  };
  const create = async (e) => {
    e.preventDefault();
    if (!draftTitle.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await http.post("/api/tasks", {
        title: draftTitle.trim(),
        due_date: draftDue || null,
        lead_id: leadId,
      });
      setDraftTitle("");
      setDraftDue("");
      setShowNew(false);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  };

  const open = tasks.filter(t => !t.completed);
  const done = tasks.filter(t => t.completed);

  return (
    <div className="card !p-4" data-testid="lead-tasks">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={14} className="text-[var(--teal-700)]" />
          <span className="text-[12px] uppercase tracking-wider text-[var(--ink-700)] font-semibold">
            Taken voor dit pand
          </span>
          {open.length > 0 && (
            <span className="text-[11px] text-[var(--ink-500)] tabular"
                  data-testid="lead-tasks-count">
              ({open.length} open)
            </span>
          )}
        </div>
        {!showNew && (
          <button type="button" onClick={() => setShowNew(true)}
                  data-testid="lead-tasks-add-btn"
                  className="flex items-center gap-1 text-[12px] text-[var(--teal-700)] hover:text-[var(--teal-900)]">
            <Plus size={13} /> Nieuwe taak
          </button>
        )}
      </div>

      {showNew && (
        <form onSubmit={create} className="mb-3 p-3 rounded-lg border border-[var(--teal-100)]
                                            bg-[var(--teal-50)] space-y-2"
              data-testid="lead-tasks-new-form">
          <input autoFocus required value={draftTitle}
                 onChange={(e) => setDraftTitle(e.target.value)}
                 placeholder="Bv. Bel eigenaar voor afspraak"
                 data-testid="lead-tasks-new-title"
                 className="w-full h-9 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-[13px]" />
          <div className="flex items-center gap-2">
            <input type="date" value={draftDue}
                   onChange={(e) => setDraftDue(e.target.value)}
                   data-testid="lead-tasks-new-due"
                   className="flex-1 h-9 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-[13px]" />
            <button type="submit" disabled={saving || !draftTitle.trim()}
                    data-testid="lead-tasks-new-save"
                    className="btn btn-primary !h-9 !px-3 !text-[12px] disabled:opacity-50">
              <Save size={12} /> {saving ? "…" : "Opslaan"}
            </button>
            <button type="button" onClick={() => { setShowNew(false); setDraftTitle(""); setDraftDue(""); setErr(null); }}
                    data-testid="lead-tasks-new-cancel"
                    className="btn btn-ghost !h-9 !px-2 !text-[12px]">
              <X size={12} />
            </button>
          </div>
          {err && <div className="text-[12px] text-[var(--danger)]">{err}</div>}
        </form>
      )}

      {loading ? (
        <div className="text-[12px] text-[var(--ink-500)]">Laden…</div>
      ) : tasks.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-500)] italic py-2" data-testid="lead-tasks-empty">
          Nog geen taken voor dit pand.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="lead-tasks-list">
          {[...open, ...done].map(task => (
            <TaskRow key={task.id} task={task}
                     onToggle={() => toggle(task)}
                     onDelete={() => remove(task)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onDelete }) {
  const dueColor = dueDateColor(task.due_date, task.completed);
  const isDone = !!task.completed;
  const onRust = !!task.archived_with_lead;
  const creatorName = task.created_by?.name;
  return (
    <li data-testid={`lead-task-${task.id}`}
        className={`flex items-start gap-2 py-1.5 px-2 rounded-lg group hover:bg-[var(--sand-50)]
                    ${isDone ? "opacity-60" : ""} ${onRust ? "bg-[var(--sand-50)]" : ""}`}>
      <button type="button" onClick={onToggle}
              data-testid={`lead-task-toggle-${task.id}`}
              className={`mt-0.5 transition-colors ${isDone ? "text-[var(--teal-600)]" : "text-[var(--ink-400)] hover:text-[var(--teal-600)]"}`}>
        {isDone ? <CheckSquare size={16} /> : <Square size={16} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] flex items-center gap-1.5 flex-wrap
                        ${isDone ? "line-through text-[var(--ink-500)]"
                                : onRust ? "text-[var(--ink-500)]"
                                : "text-[var(--ink-900)]"}`}>
          <span>{task.title}</span>
          {onRust && !isDone && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full
                             text-[9px] uppercase tracking-wider font-semibold
                             bg-[var(--sand-100)] text-[var(--ink-500)] border border-[var(--border-soft)]"
                  title="Deze taak staat mee op rust en verschijnt niet in /taken tot de lead terugkeert">
              <Moon size={9} /> Op rust
            </span>
          )}
        </div>
        {creatorName && (
          <div className="text-[10px] text-[var(--ink-500)] mt-0.5">
            door {creatorName}
          </div>
        )}
      </div>
      {task.due_date && (
        <div className={`text-[11px] font-medium tabular shrink-0 flex items-center gap-1 mt-0.5 ${dueColor}`}
             data-testid={`lead-task-due-${task.id}`}>
          <Calendar size={10} />
          {formatDue(task.due_date)}
        </div>
      )}
      <button type="button" onClick={onDelete}
              data-testid={`lead-task-delete-${task.id}`}
              title="Verwijderen"
              className="opacity-0 group-hover:opacity-100 p-1 rounded text-[var(--ink-400)] hover:bg-[var(--sand-100)] hover:text-[var(--danger)] transition-opacity">
        <Trash2 size={12} />
      </button>
    </li>
  );
}

// ---------- date helpers (kept local to avoid cross-import) ----------
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dueDateColor(due_date, completed) {
  if (!due_date) return "text-[var(--ink-500)]";
  if (completed) return "text-[var(--ink-400)]";
  const today = startOfDay(new Date());
  const parsed = parseDate(due_date);
  if (!parsed) return "text-[var(--ink-500)]";
  const d = startOfDay(parsed);
  if (d < today) return "text-[var(--danger,#D6646E)]";
  if (d.getTime() === today.getTime()) return "text-[#D97706]";
  return "text-[var(--ink-500)]";
}

function formatDue(due_date) {
  if (!due_date) return "";
  const today = startOfDay(new Date());
  const parsed = parseDate(due_date);
  if (!parsed) return "—";
  const d = startOfDay(parsed);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Morgen";
  if (diff === -1) return "Gisteren";
  if (diff < 0) return `${Math.abs(diff)}d te laat`;
  if (diff < 7) return `+${diff}d`;
  return parsed.toLocaleDateString("nl-BE", { day: "2-digit", month: "short" });
}
